import { AuthPageSkeleton } from "@/components/loading";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { SkeletonDashboard } from "@/components/skeleton-dashboard";
import { useSession } from "@/hooks/use-session";
import { getBootstrapRouteError } from "@/lib/bootstrap-routing";
import { useTRPC } from "@/lib/trpc";
import { useBrandStore } from "@/state/store/brand";
import type { Brand } from "@/types/data";

export const Route = createFileRoute("/")({
      component: IndexRoute,
});

function IndexRoute() {
      const { sessionData, isLoading } = useSession();
      const navigate = useNavigate();
      const selectedBrandId = useBrandStore((state) => state.selectedBrandId);

      const trpc = useTRPC();
      const { data: brands, isLoading: isLoadingBrands, error: brandsError } = useQuery({
            ...trpc.brands.getOrganizationBrands.queryOptions({
                  skip: 0,
                  take: 100,
            }),
            enabled: !!sessionData,
      });

      // Fetch personas for the first brand if there is exactly one brand
      const { data: personas, isLoading: isLoadingPersonas, error: personasError } = useQuery({
            ...trpc.personas.getBrandPersonas.queryOptions({
                  brandId: brands?.[0]?.id || "",
            }),
            enabled: !!sessionData && brands?.length === 1,
      });

      // Fetch search specifications for the first brand to check for prompts
      const { data: searchSpecs, isLoading: isLoadingSearchSpecs, error: searchSpecsError } = useQuery({
            ...trpc.searchSpecifications.getBrandSearchSpecifications.queryOptions({
                  brandId: brands?.[0]?.id || "",
                  skip: 0,
                  take: 1,
            }),
            enabled: !!sessionData && brands?.length === 1 && !!personas?.length,
      });

      // Fetch prompts if there is a search specification
      const { data: prompts, isLoading: isLoadingPrompts, error: promptsError } = useQuery({
            ...trpc.prompts.getSearchSpecificationPrompts.queryOptions({
                  searchSpecificationId: searchSpecs?.items?.[0]?.id || "",
            }),
            enabled: !!sessionData && brands?.length === 1 && !!searchSpecs?.items?.length,
      });

      const {
            data: hasSearchExecutions,
            isLoading: isLoadingSearchExecutions,
            error: hasSearchExecutionsError,
      } = useQuery({
            ...trpc.brands.hasAnyBrandWithSearchExecutions.queryOptions({}),
            enabled: !!sessionData && brands?.length === 1,
      });

      const bootstrapError = getBootstrapRouteError([
            {
                  isEnabled: !!sessionData,
                  isLoading: isLoadingBrands,
                  error: brandsError ?? null,
            },
            {
                  isEnabled: !!sessionData && brands?.length === 1,
                  isLoading: isLoadingPersonas,
                  error: personasError ?? null,
            },
            {
                  isEnabled: !!sessionData && brands?.length === 1 && !!personas?.length,
                  isLoading: isLoadingSearchSpecs,
                  error: searchSpecsError ?? null,
            },
            {
                  isEnabled: !!sessionData && brands?.length === 1 && !!searchSpecs?.items?.length,
                  isLoading: isLoadingPrompts,
                  error: promptsError ?? null,
            },
            {
                  isEnabled: !!sessionData && brands?.length === 1,
                  isLoading: isLoadingSearchExecutions,
                  error: hasSearchExecutionsError ?? null,
            },
      ]);

      if (bootstrapError) {
            throw bootstrapError;
      }

      useEffect(() => {
            // Redirect to auth if no session
            if (!sessionData && !isLoading) {
                  navigate({ to: "/auth", replace: true });
                  return;
            }

            // Wait for brands to load
            if (!sessionData || isLoadingBrands) return;

            if (brands?.length === 0) {
                  navigate({ to: "/onboarding/brand/new", replace: true });
                  return;
            }

            // Smart redirection for abandoned onboarding
            if (brands?.length === 1) {
                  // Wait for all onboarding checks to load
                  if (isLoadingPersonas || isLoadingSearchSpecs || isLoadingPrompts || isLoadingSearchExecutions) return;

                  const brand = brands[0];
                  const hasPersonas = personas && personas.length > 0;
                  
                  // If they have search executions, they've completed onboarding
                  if (!hasSearchExecutions) {
                        if (!hasPersonas) {
                              navigate({
                                    to: "/onboarding/brand/$brandId/persona",
                                    params: { brandId: brand.id },
                                    replace: true,
                              });
                              return;
                        }

                        // If they have a persona (and optionally prompts), but haven't started a search
                        const searchSpecId = searchSpecs?.items?.[0]?.id;
                        navigate({
                              to: "/onboarding/brand/$brandId/prompts",
                              params: { brandId: brand.id },
                              search: searchSpecId ? { searchSpecificationId: searchSpecId } : undefined,
                              replace: true,
                        });
                        return;
                  }
            }

            // If we have brands, navigate to the appropriate one
            if (brands && brands.length > 0) {
                  let targetBrand: Brand | undefined;

                  // If we have a selectedBrandId, validate it exists
                  if (selectedBrandId) {
                        targetBrand = brands.find((brand) => brand.id === selectedBrandId);

                        if (!targetBrand) {
                              console.warn(
                                    `[Navigation] selectedBrandId "${selectedBrandId}" not found. Using first brand.`,
                              );
                              targetBrand = brands[0];
                        }
                  } else {
                        targetBrand = brands[0];
                  }

                  // Navigate to the brand dashboard
                  if (targetBrand?.slug) {
                        navigate({
                              to: "/$brand/dashboard",
                              params: { brand: targetBrand.slug },
                              replace: true,
                        });
                  } else {
                        console.error("[Navigation] Target brand missing slug", targetBrand);
                  }
            }
      }, [
            sessionData,
            brands,
            selectedBrandId,
            navigate,
            isLoading,
            isLoadingBrands,
            personas,
            searchSpecs,
            prompts,
            hasSearchExecutions,
            isLoadingPersonas,
            isLoadingSearchSpecs,
            isLoadingPrompts,
            isLoadingSearchExecutions,
      ]);

      if (!sessionData) {
            return <AuthPageSkeleton />;
      }

      return <SkeletonDashboard />;
}
